import { supabase } from '@/lib/supabase/client';
import type {
  LaunchComponentRequirementOverride,
  LaunchComponentRequirementOverrideInput,
} from '@/types/launch-component-requirements';

function migrationRequiredMessage(message: string): string {
  return message.includes('launch_component_requirement_overrides')
    ? 'Launch component requirements are not available yet. Apply migration 20260728194500_launch_component_requirement_overrides.sql first.'
    : message;
}

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export const launchComponentRequirementsService = {
  async getByLaunchId(launchId: string): Promise<LaunchComponentRequirementOverride[]> {
    const { data, error } = await supabase
      .from('launch_component_requirement_overrides')
      .select('*')
      .eq('launch_id', launchId);

    if (error) {
      throw new Error(`Failed to load launch component requirements: ${migrationRequiredMessage(error.message)}`);
    }

    return (data ?? []) as LaunchComponentRequirementOverride[];
  },

  async save(input: LaunchComponentRequirementOverrideInput): Promise<LaunchComponentRequirementOverride> {
    const userId = await getCurrentUserId();
    const { data: existing, error: existingError } = await supabase
      .from('launch_component_requirement_overrides')
      .select('created_by')
      .eq('launch_id', input.launch_id)
      .eq('component_id', input.component_id)
      .maybeSingle();

    if (existingError) {
      throw new Error(`Failed to load launch component requirement: ${migrationRequiredMessage(existingError.message)}`);
    }

    const { data, error } = await supabase
      .from('launch_component_requirement_overrides')
      .upsert(
        {
          ...input,
          notes: input.notes?.trim() || null,
          created_by: existing?.created_by ?? userId,
          updated_by: userId,
        },
        { onConflict: 'launch_id,component_id' }
      )
      .select()
      .single();

    if (error || !data) {
      throw new Error(
        `Failed to save launch component requirement: ${migrationRequiredMessage(error?.message ?? 'No record returned')}`
      );
    }

    return data as LaunchComponentRequirementOverride;
  },

  async remove(launchId: string, componentId: string): Promise<void> {
    const { error } = await supabase
      .from('launch_component_requirement_overrides')
      .delete()
      .eq('launch_id', launchId)
      .eq('component_id', componentId);

    if (error) {
      throw new Error(`Failed to restore BOM requirement: ${migrationRequiredMessage(error.message)}`);
    }
  },
};
